<template>
  <div>
    <ca-heading text="Cadastro de evento" />

    <ca-page class="ca-u-margin-top">
      <ca-loader v-if="showLoading"></ca-loader>

      <ca-form
        v-else
        :on-submit="onSubmit"
        :on-submit-success="onSubmitSuccess"
        :on-submit-error="onSubmitError"
        :error-message="errorMessage">
        <ca-row>
          <ca-col size="4">
            <ca-field label="Natureza">
              <ca-select v-model="event.eventNature.name" required>
                <ca-option
                  v-for="option in misc.eventsNature"
                  :key="option.name"
                  :value="option.name">
                  {{ option.description }}
                </ca-option>
              </ca-select>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Código">
              <ca-input
                v-model="event.code"
                mask-pattern="999"
                max="999"
                type="number"
                required>
              </ca-input>
            </ca-field>
          </ca-col>

          <ca-col size="2" class="ca-u-margin-top">
            <ca-button full-width @click="onGetLastCode">Buscar último</ca-button>
          </ca-col>

          <ca-col size="2">
            <ca-field
              label="Compartilhado"
              icon-tooltip="Os eventos podem ser compartilhados apenas no momento da criação.">
              <ca-checkbox
                v-model="event.shared"
                label="Sim"
                :disabled="!fromCopy"></ca-checkbox>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row>
          <ca-col size="12">
            <ca-field label="Descrição">
              <ca-input
                v-model="event.description"
                maxlength="100"
                required></ca-input>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row>
          <ca-col size="3">
            <ca-field
              label="Fator multiplicador"
              icon-tooltip="Ao ser calculado, o evento será multiplicado
             por este fator. Ex.: Horas extras 70% tem fator multiplicador = 1,7">
              <percent-input
                v-model="event.multiplierFactor"
                :integer-places="1"
                :decimal-places="4"
                required>
              </percent-input>
            </ca-field>
          </ca-col>
          <ca-col size="3">
            <ca-field label="Base do DSR">
              <ca-checkbox
                v-model="event.dsrBasis"
                label="Sim"
                @change="onBaseDsrChange">
              </ca-checkbox>
            </ca-field>
          </ca-col>
          <ca-col v-if="event.dsrBasis && event.dsrEvent" size="6">
            <ca-field label="Evento do DSR">
              <ca-search-select v-model="event.dsrEvent.id" :selected-option="selectedEventDsr">
                <ca-option
                  :key="EMPTY_DSR.value"
                  :value="EMPTY_DSR.value">
                  {{ EMPTY_DSR.label }}
                </ca-option>
                <ca-option
                  v-for="option in events"
                  :key="option.id"
                  :value="option.id">
                  {{ `${option.code} - ${option.description}` }}
                </ca-option>
              </ca-search-select>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row>
          <ca-col size="3">
            <ca-field label="Base do INSS">
              <ca-checkbox v-model="event.inssBasis" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Base do IRRF">
              <ca-checkbox v-model="event.irrfBasis" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Base do FGTS">
              <ca-checkbox v-model="event.fgtsBasis" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>
          <ca-col size="3">
            <ca-field label="Base da contribuição sindical">
              <ca-checkbox
                v-model="event.unionContributionBasis"
                label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-row>
          <ca-col size="3">
            <ca-field label="Calcular nas férias">
              <ca-checkbox v-model="event.calculateOnVacation" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Calcular no 13º salário">
              <ca-checkbox v-model="event.calculateOnThirteenthSalary" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>

          <ca-col size="3">
            <ca-field label="Calcular no aviso prévio">
              <ca-checkbox v-model="event.calculateAtNotice" label="Sim"></ca-checkbox>
            </ca-field>
          </ca-col>
        </ca-row>

        <ca-fieldset title="eSocial">
          <ca-row>
            <ca-col size="6">
              <ca-field
                label="Natureza da Rubrica"
                icon-tooltip="Código na tabela de Rubricas do eSocial">
                <ca-input
                  v-model="event.esocialCode"
                  maxlength="4"
                  required></ca-input>
              </ca-field>
            </ca-col>
            <ca-col size="6">
              <ca-field
                label="Código de Incidência INSS">
                <ca-input
                  v-model="event.inssTaxIncidenceCode"
                  required
                  mask-pattern="99"
                  :custom-validations="customValidations" />
              </ca-field>
            </ca-col>
            <ca-col size="4">
              <ca-field
                label="Código de Incidência IRRF">
                <ca-input
                  v-model="event.irrfTaxIncidenceCode"
                  required
                  mask-pattern="9999"
                  :custom-validations="customValidationsIrrf" />
              </ca-field>
            </ca-col>
            <ca-col size="4">
              <ca-field
                label="Código de Incidência FGTS">
                <ca-input
                  v-model="event.fgtsTaxIncidenceCode"
                  required
                  mask-pattern="99"
                  :custom-validations="customValidations" />
              </ca-field>
            </ca-col>
            <ca-col size="4">
              <ca-field
                label="Código de Incidência RPPS">
                <ca-input
                  v-model="event.rppsTaxIncidenceCode"
                  required
                  mask-pattern="99"
                  :custom-validations="customValidations" />
              </ca-field>
            </ca-col>
            <ca-col size="12">
              <ca-field label="Observação">
                <ca-input
                  v-model="event.observation"
                  minlength="3"
                  maxlength="255"></ca-input>
              </ca-field>
            </ca-col>
          </ca-row>
        </ca-fieldset>
        <br />
        <ca-row>
          <ca-col>
            <ca-heading
              text="Eventos base"
              size="small">
            </ca-heading>
          </ca-col>
          <ca-col>
            <ca-table>
              <ca-tr slot="header">
                <ca-th>Código/Descrição</ca-th>
                <ca-th width="20%" align="right"></ca-th>
              </ca-tr>
              <ca-tr
                v-for="(baseEvent, index) in event.baseEvents"
                :key="index">
                <ca-td with-input="input">
                  <ca-search-select v-model="baseEvent.id">
                    <ca-option
                      v-for="option in events"
                      :key="option.id"
                      :value="option.id">
                      {{ `${option.code} - ${option.description}` }}
                    </ca-option>
                  </ca-search-select>
                </ca-td>
                <ca-td align="center">
                  <ca-button
                    size="small"
                    @click="onDeleteBaseEvent(index)">
                    Excluir
                  </ca-button>
                </ca-td>
              </ca-tr>
            </ca-table>
          </ca-col>
        </ca-row>

        <ca-row vertical-offset="2" class="ca-u-min-margin-bottom">
          <ca-col size="3">
            <ca-button
              theme="secondary"
              size="small"
              @click="newBaseEvent">
              Adicionar evento base
            </ca-button>
          </ca-col>
        </ca-row>

        <ca-footer>
          <ca-row vertical-offset="2">
            <ca-col size="2">
              <ca-button full-width @click="goBack">
                Voltar
              </ca-button>
            </ca-col>
            <ca-col offset="8" size="2">
              <ca-submit-button>
                Salvar
              </ca-submit-button>
            </ca-col>
          </ca-row>
        </ca-footer>
      </ca-form>
    </ca-page>
  </div>
</template>

<script>
import { getBoxInformationErrorMessage, initFormObject } from 'packages/common/utils';
import { EMPTY_DSR } from './constants';
import {
  getEvent, createEvent, editEvent, getLastCode, getShortEvents, checkIsMaxLength,
} from './eventService';

export default {
  name: 'EventFormPage',
  data() {
    return {
      eventId: this.$route.params.id,
      fromCopy: this.$route.params.fromCopy,
      showLoading: false,
      event: {
        ...initFormObject([{ eventNature: 'P' }]),
        multiplierFactor: 1,
        baseEvents: [],
        dsrEvent: { id: null },
      },
      selectedEventDsr: null,
      errorMessage: null,
      events: {},
      EMPTY_DSR,
      customValidations: [{
        valid: value => {
          return checkIsMaxLength(2, value);
        },
        message: 'Deve conter no máximo 2 dígitos',
      }],
      customValidationsIrrf: [{
        valid: value => {
          return checkIsMaxLength(4, value);
        },
        message: 'Deve conter no máximo 4 dígitos',
      }],
    };
  },
  computed: {
    hasEventId() {
      return this.eventId;
    },
    misc() {
      return this.$store.state.misc;
    },
  },
  watch: {
    event() {
      if (this.event.baseEvents.length === 0) {
        this.newBaseEvent();
      }
    },
  },
  created() {
    this.initData();
  },
  methods: {
    initData() {
      this.$store.dispatch('misc/getEsocialStatus');
      this.$store.dispatch('misc/getEventsNature');
      this.setEvents();

      if (this.hasEventId) {
        this.setEvent();
      } else {
        this.newBaseEvent();
      }
    },
    async setEvent() {
      this.showLoading = true;
      try {
        this.event = await getEvent(this.eventId);
        if (this.fromCopy) {
          this.$set(this.event, 'parentCode', this.event.code);
          await this.onGetLastCode();
        }
      } catch (e) {
        this.$toaster.error('Aconteceu um erro, por favor tente novamente.');
      } finally {
        this.showLoading = false;
      }
    },
    goBack() {
      this.$router.back();
    },
    onBaseDsrChange() {
      if (this.event.dsrBasis) {
        this.$set(this.event.dsrEvent, 'id', EMPTY_DSR.value);
        this.selectedEventDsr = EMPTY_DSR;
      } else {
        this.event.dsrEvent = { id: null };
      }
    },
    onSubmit() {
      this.cleanEventId();
      return this.hasEventId
        ? editEvent(this.eventId, this.event)
        : createEvent(this.event);
    },
    cleanEventId() {
      if (this.fromCopy) {
        this.eventId = null;
      }
    },
    onSubmitSuccess({ data }) {
      this.$router.push({ name: 'EventView', params: { id: this.fromCopy ? data?.id : this.eventId } });
      this.$toaster.success('O registro foi gravado com sucesso');
    },
    onSubmitError(error) {
      this.errorMessage = getBoxInformationErrorMessage(error);
    },
    async setEvents() {
      this.events = await getShortEvents();
    },
    onGetLastCode() {
      if (!this.event.eventNature.name) {
        return this.$toaster.error('Selecione uma natureza de atividade antes.');
      }

      return getLastCode(this.event.eventNature.name)
        .then(response => {
          this.$set(this.event, 'code', response);
        })
        .catch(() => {
          this.$toaster.error('Erro ao buscar código.');
        });
    },
    onDeleteBaseEvent(index) {
      this.$delete(this.event.baseEvents, index);
    },
    newBaseEvent() {
      this.event.baseEvents.push({});
    },
  },
};
</script>
